import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Phone, TriangleAlert } from 'lucide-react';

import { Alert } from '@/components/ui/Alert';
import { Button } from '@/components/ui/Button';
import { Spinner } from '@/components/ui/Spinner';
import { formatDateTime, formatRelative } from '@/lib/format';
import { CompleteTaskDialog } from './CompleteTaskDialog';
import { useMyTasks, type TaskRow } from './useTasks';

export default function MyTasksPage() {
  const { t } = useTranslation();
  const [scope, setScope] = useState<'open' | 'overdue'>('open');
  const [closing, setClosing] = useState<TaskRow | null>(null);

  const { data, isPending, isError } = useMyTasks(scope);
  const tasks = data ?? [];

  return (
    <section className="flex flex-col gap-4">
      <h1 className="text-xl font-bold text-slate-900">{t('nav.tasks')}</h1>

      <div className="flex gap-2">
        {(['open', 'overdue'] as const).map((s) => (
          <button
            key={s}
            type="button"
            onClick={() => setScope(s)}
            aria-pressed={scope === s}
            className={
              scope === s
                ? 'rounded-lg bg-brand-700 px-3 py-1.5 text-sm font-medium text-white'
                : 'rounded-lg px-3 py-1.5 text-sm font-medium text-slate-600 hover:bg-slate-100'
            }
          >
            {s === 'open' ? t('tasks.allOpen') : t('tasks.overdueOnly')}
          </button>
        ))}
      </div>

      {isError ? <Alert tone="error">{t('common.error')}</Alert> : null}

      {isPending ? (
        <Spinner />
      ) : tasks.length === 0 ? (
        <div className="rounded-xl border border-slate-200 bg-white p-8 text-center text-sm text-slate-500">
          {scope === 'overdue' ? t('tasks.noneOverdue') : t('tasks.noneOpen')}
        </div>
      ) : (
        <ul className="flex flex-col gap-2">
          {tasks.map((task) => {
            const overdue = task.is_overdue;
            return (
              <li
                key={task.id}
                className={`flex flex-wrap items-center gap-3 rounded-xl border bg-white p-3 ${
                  overdue ? 'border-red-200' : 'border-slate-200'
                }`}
              >
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2">
                    {overdue ? (
                      <TriangleAlert className="size-4 shrink-0 text-red-600" aria-hidden="true" />
                    ) : null}
                    <Link
                      to={`/leads/${task.lead_id}`}
                      className="truncate font-medium text-brand-800 hover:underline"
                    >
                      {task.leads?.full_name ?? '—'}
                    </Link>
                  </div>

                  <div className="mt-0.5 flex flex-wrap items-center gap-x-3 text-xs text-slate-500">
                    <span>{t(`activities.${task.type}` as 'activities.call')}</span>
                    <span className={overdue ? 'font-medium text-red-700' : ''}>
                      {formatRelative(task.due_at)} · {formatDateTime(task.due_at)}
                    </span>
                    {task.escalation_level > 0 ? (
                      <span className="text-red-700">{t('tasks.escalated')}</span>
                    ) : null}
                  </div>

                  {task.note ? (
                    <p className="mt-0.5 truncate text-xs text-slate-600">{task.note}</p>
                  ) : null}
                </div>

                {/* One-tap dial straight from the queue: the point of this screen is
                    that a rep can work it without opening each lead first. */}
                {task.leads?.phone_primary ? (
                  <a
                    href={`tel:${task.leads.phone_primary}`}
                    aria-label={t('leads.call')}
                    className="rounded-lg border border-slate-300 p-2 text-slate-700 hover:bg-slate-50"
                  >
                    <Phone className="size-4" aria-hidden="true" />
                  </a>
                ) : null}

                <Button variant="secondary" onClick={() => setClosing(task)}>
                  {t('tasks.complete')}
                </Button>
              </li>
            );
          })}
        </ul>
      )}

      <CompleteTaskDialog task={closing} onClose={() => setClosing(null)} />
    </section>
  );
}
